import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
} from "@mui/material";
import { Formik, Form } from "formik";
import dayjs from "dayjs";
import "./add-campaign-popup.scss"; // Create if needed
import leadInfoIcon from "../../../../../../../assets/icons/leadInfo.svg";
import leadDetailsIcon from "../../../../../../../assets/icons/leadDetails.svg";
import descriptionIcon from "../../../../../../../assets/icons/description.svg";
import { getUserDetails } from "../../../../../../../api/lead";
import { getCurrentUser } from "../../../../../../../common/routines/user-operations";
import { campainCreate } from "../../../../../../../api/campaign";
import { useSetLoading } from "../../../../../../../common/hooks/appearance";
import { AlertComponent, CardComponent, DescriptionInputComponent, RevenueInputComponent, SelectInputComponent, TextInputComponent } from "../../../../../../../common/components";
import SearchableSelectOwner from "../../../../../../../common/components/select-owner/select-owner-compoent";
import DatePickerComponent from "../../../../../../../common/components/date-picker/date-picker-compoenent";
import { TYPE_OPTIONS } from "../../../../../../../constants/campaignDropdown";
import { STATUS_OPTIONS } from "../../campaign-view-details-view-activities-info/common-dialog-task/common-dialog-helper";
import { initialValues, validationSchema } from "../../../../../create-campaign/create-campaign-helper";

interface OwnerOption {
  value: string;
  label: string;
  email: string;
  avatar?: string | null;
}

interface AddCampaignPopupProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onSuccessCallback?: (
    msg: string,
    type: "success" | "error",
    campaign?: { id: number; name: string }
  ) => void;
}

const AddCampaignPopup: React.FC<AddCampaignPopupProps> = ({
  open,
  onClose,
  onSuccess,
  onSuccessCallback,
}) => {
  const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
  const [formInitialValues, setFormInitialValues] = useState(initialValues);
  const [selectedCampaignStatus, setSelectedCampaignStatus] =
    useState("Planning");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [isLoading, setLoading] = useState<boolean>(false);

  const handleAlertClose = () => setAlertOpen(false);

  useEffect(() => {
    const fetchCreatedByOptions = async () => {
      try {
        const response = await getUserDetails({}, "Lead Details");
        const data = response.data.data.data.data;
        const formattedOptions: OwnerOption[] = data.map((user: any) => ({
          value: String(user.id),
          label: `${user.first_name} ${user.last_name}`,
          email: user.email,
          avatar: user.profile_image_url || null,
        }));

        setLeadOptions(formattedOptions);

        // Set default campaign owner
        const currentUserId = getCurrentUser();
        const matchedUser = formattedOptions.find(
          (opt) => opt.value === String(currentUserId?.id)
        );
        if (matchedUser) {
          setFormInitialValues((prev) => ({
            ...prev,
            campaignOwner: matchedUser.value,
          }));
        }
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
            "Something went wrong please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
      }
    };

    fetchCreatedByOptions();
  }, []);
  const handleCancel = () => {
    onClose();
  };
  const handleSave = async (values: any, resetForm: any) => {
    setLoading(true);
    try {
      const campaignDetails = {
        campaignOwnerId: values.campaignOwner,
        campaignName: values.campaignName,
        startDate: values.startdate
          ? values.startdate
          : null,
        endDate: values.enddate ? values.enddate : null,
        type: values.type || null,
        status: selectedCampaignStatus || null,
        budgetedCost: values.budgetCost || null,
        actualCost: values.actualCost || null,
        expectedRevenue: values.expectedRevenue || null,
        expectedResponse: values.expectedResponse || null,
        description: values.description || null,
      };

      const response = await campainCreate(campaignDetails, "Lead Details");

      if (response.data.success) {
        const createdCampaign = response.data.data;
        resetForm();
        setSelectedCampaignStatus("Planning");
        setLoading(false);
        onClose();
        onSuccessCallback?.("Campaign created successfully!", "success", {
          id: createdCampaign.id,
          name: createdCampaign.name,
        });
      }
    } catch (error) {
      setAlertMessage("An error occurred while saving the campaign.");
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ lineHeight: 1, fontSize: "19px", fontWeight: 600 }}>
        Create Campaign
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ backgroundColor: "#f4f4f4", paddingBottom: 0 }}
      >
        <Formik
          enableReinitialize
          initialValues={formInitialValues}
          validationSchema={validationSchema(false)}
          onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
        >
          {({ values, setFieldValue, setFieldTouched, handleSubmit }) => (
            <Form>
              <div className="popup-campiagn-wrapper">
                <div className="campaign-form-wrapper">
                  <CardComponent
                    title="Campaign Information"
                    icon={leadDetailsIcon}
                  >
                    <div className="form-grid">
                      <SearchableSelectOwner
                        fieldName="campaignOwner"
                        options={leadOptions}
                        isShowError={!values?.campaignOwner}
                        placeholder="Select Owner"
                        value={
                          leadOptions.find(
                            (opt) => opt.value === values?.campaignOwner
                          ) || null
                        }
                        onChange={(selectedOption: OwnerOption | null) => {
                          if (selectedOption) {
                            setFieldValue(
                              "campaignOwner",
                              selectedOption.value
                            ); // saving only the ID
                          } else {
                            setFieldValue("campaignOwner", null); // handle null case
                          }
                        }}
                        handleBlur={() => {
                          setFieldTouched("campaignOwner", true);
                        }}
                        label="Campaign Owner"
                      />
                      <TextInputComponent
                        type="text"
                        fieldName="campaignName"
                        label="Campaign Name"
                        placeholder="Enter Campaign Name"
                        optional={true}
                        disabled={false}
                        value={values.campaignName}
                        onChange={(e: any) =>
                          setFieldValue("campaignName", e?.target?.value)
                        }
                        maxlength="100"
                      />
                      <DatePickerComponent
                        fieldName="startdate"
                        label="Start Date"
                        value={values?.startdate || null}
                        onChange={(date) => setFieldValue("startdate", date)}
                        minDate={dayjs("1900-01-01")}
                        maxDate={values?.enddate || undefined}
                        optional={true}
                      />
                      <DatePickerComponent
                        fieldName="enddate"
                        label="End Date"
                        value={values?.enddate || null}
                        onChange={(date) => setFieldValue("enddate", date)}
                        minDate={
                          values?.startdate
                            ? dayjs(values.startdate).isBefore(
                                dayjs("1900-01-01")
                              )
                              ? dayjs("1900-01-01")
                              : dayjs(values.startdate)
                            : undefined
                        }
                        optional={true}
                      />
                      <SelectInputComponent
                        fieldName="type"
                        label="Type"
                        value={values?.type}
                        onChange={(e: any) => {
                          setFieldValue("type", e);
                        }}
                        options={TYPE_OPTIONS}
                        optional={true}
                        isShowError={!values.type}
                        placeholder="Select Type"
                      />
                      <SelectInputComponent
                        fieldName="status"
                        label="Status"
                        options={STATUS_OPTIONS}
                        onChange={(value: string) => {
                          setFieldValue("status", value);
                          setSelectedCampaignStatus(value);
                        }}
                        value={selectedCampaignStatus}
                      />
                      <RevenueInputComponent
                        fieldName="expectedRevenue"
                        label="Expected Revenue"
                        optional={false}
                        disabled={false}
                        value={values.expectedRevenue}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
                            setFieldValue("expectedRevenue", input);
                          }
                        }}
                        logo={leadInfoIcon}
                        placeholder=""
                      />
                      <TextInputComponent
                        type="text"
                        fieldName="expectedResponse"
                        label="Expected Response"
                        placeholder="Enter Expected Response"
                        optional={false}
                        disabled={false}
                        value={values.expectedResponse}
                        onChange={(e: any) =>
                          setFieldValue("expectedResponse", e?.target?.value)
                        }
                        maxlength="50"
                      />
                      <RevenueInputComponent
                        fieldName="budgetCost"
                        label="Budgeted Cost"
                        optional={false}
                        disabled={false}
                        value={values.budgetCost}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
                            setFieldValue("budgetCost", input);
                          }
                        }}
                        logo={leadInfoIcon}
                        placeholder=""
                      />
                      <RevenueInputComponent
                        fieldName="actualCost"
                        label="Actual Cost"
                        optional={false}
                        disabled={false}
                        value={values.actualCost}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
                            setFieldValue("actualCost", input);
                          }
                        }}
                        logo={leadInfoIcon}
                        placeholder=""
                      />
                    </div>
                  </CardComponent>
                  <div className="card-container description-container">
                    <CardComponent title="Description" icon={descriptionIcon}>
                      <div className="form-grid-three">
                        <DescriptionInputComponent
                          fieldName="description"
                          label="Company Description"
                          placeholder="Add Description"
                          optional={false}
                          disabled={false}
                          value={values.description}
                          onChange={(e: any) =>
                            setFieldValue("description", e?.target?.value)
                          }
                          maxlength={150}
                        />
                      </div>
                    </CardComponent>
                  </div>
                </div>
              </div>
              <AlertComponent
                message={alertMessage}
                type={alertType}
                open={alertOpen}
                onClose={handleAlertClose}
              />
              <Box className="footer">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="cancel-button next-button-wrapper"
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  Save
                </button>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AddCampaignPopup;
